import React, { useState, useRef, useEffect } from 'react';
import { Button, TextField, Checkbox, FormControlLabel, FormGroup, Box, Typography, CircularProgress } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import axios from 'axios';
import { toast } from 'react-toastify';

const EditBrandForm = () => {
  const { id } = useParams(); 
  const [formData, setFormData] = useState({
    title: '',
    image: null,
    publish: false,
  });
  const [loading, setLoading] = useState(false);  // Loading state for fetching data
  const [submitting, setSubmitting] = useState(false);  // Loading state for submitting data
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);

  // Fetch brand data by ID when the component mounts or when the ID changes
  useEffect(() => {
    const fetchBrandData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/brands/getbrandbyid/${id}` // Fetch the brand data by ID
        );
        const brand = response.data.brands;
        setFormData({
          title: brand.title,
          publish: brand.publish,
          image: brand.image || null,  // Make sure image is fetched
        });
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch brand data');
        setLoading(false);
      }
    };
    
    fetchBrandData();
  }, [id]); // Fetch brand data whenever the ID changes

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData({
      ...formData,
      image: file,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    data.append('title', formData.title);
    data.append('publish', formData.publish);
    if (formData.image) {
      data.append('image', formData.image);
    }

    setSubmitting(true);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/brands/edit/${id}`, 
        data,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      console.log(response.data);
      toast.success(`${response.data.message}`);
      setFormData({
        title: '',
        image: null,
        publish: false,
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSubmitting(false);
    } catch (error) {
      console.log(error);
      toast.error('Brand is not updated');
      setSubmitting(false);
    }
  };

  // Show loading or error if necessary
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ marginLeft: 2 }}>Edit brand loading...</Typography>
              </Box>
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <Box
      sx={{
        maxWidth: {
          xs: '100%',  
          md: '70%',  
        },
        margin: {xs:'0', md:'auto'},
        padding: {xs:0, md:2},
      }}
    >
      <form onSubmit={handleSubmit}>
        <fieldset
          style={{
            border: '1px solid #ccc',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
          }}
        >
          <legend
            style={{
              padding: '8px',
              width: 'auto',
              background: '#3C7F10',
              color: '#fff',
              borderRadius: '8px',
            }}
          >
            Edit Brand
          </legend>

          <Box sx={{ marginBottom: { xs: 1, md: 2 }, width: { xs: '100%' } }}>
            <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
              Name*
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              helperText="Insert Name"
              className="input-field-cls"
            />
          </Box>

          <Box sx={{ marginBottom: 2, width: { xs: "100%", md: "100%" } }}>
            <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
              Image*
            </Typography>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ marginBottom: '10px' }}
              ref={fileInputRef}
            />

            {formData.image && !formData.image.name && (
              <Box sx={{ marginTop: '5px' }}>
                <img
                  src={`${formData.image}`} // Handle image URL properly
                  alt="Current Brand"
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover', 
                    marginTop: '5px',
                    borderRadius: '4px', 
                  }}
                />
              </Box>
            )}

            <Typography variant="body2" color="textSecondary" style={{ marginTop: '5px' }}>
              Insert image in jpg/png format
            </Typography>
          </Box>

          <Box sx={{ marginBottom: 2, width: { xs: '100%', md: '100%' } }}>
            <FormGroup sx={{ marginBottom: 1, fontWeight: '500' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.publish}
                    onChange={handleChange}
                    name="publish"
                  />
                }
                label="Publish"
              />
            </FormGroup>
          </Box>
        </fieldset>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button variant="contained" sx={{ bgcolor: 'green', color: '#fff' }} type="submit" disabled={submitting}>
            <SaveIcon sx={{ pr: 1 }} />
            {submitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save'}
          </Button>
          <Link to="/brands" style={{ textDecoration: 'none' }}>
            <Button variant="contained" sx={{ bgcolor: '#979797', color: '#fff' }}>
              <SettingsBackupRestoreIcon sx={{ pr: 1 }} />
              Back
            </Button>
          </Link>
        </Box>
      </form>
    </Box>
  );
};

export default EditBrandForm;
